// Persist Meta click identifiers (_fbc / _fbp) onto the cart so they survive onto
// the order record.
//
// Why: in a cash-on-delivery market the outcome that matters (delivered vs refused)
// is only known days after the order. Uploading that outcome back to Meta as an
// offline conversion requires matching it to the original ad click, so the click id
// has to be stored somewhere durable at order time. The pixel's own cookies are gone
// by then.
//
// AddToCart is deliberately NOT tracked here — the Meta web pixel installed by the
// Facebook sales channel already fires it.

/** Read _fbc / _fbp from document.cookie. */
function readMetaCookies() {
  /** @type {Record<string, string>} */
  const found = {};
  const pattern = /(?:^|;\s*)(_fbc|_fbp)=([^;]*)/g;
  let match;

  while ((match = pattern.exec(document.cookie || '')) !== null) {
    found[match[1]] = decodeURIComponent(match[2]);
  }

  return found;
}

/**
 * Rebuild _fbc from ?fbclid when the pixel hasn't written the cookie yet (slow
 * network, blocked script, in-app browser). Format is Meta's: fb.<subdomain>.<ms>.<fbclid>
 * @returns {string | null}
 */
function deriveFbcFromUrl() {
  const fbclid = new URLSearchParams(window.location.search).get('fbclid');
  return fbclid ? `fb.1.${Date.now()}.${fbclid}` : null;
}

async function persistClickIds() {
  const cookies = readMetaCookies();
  const fbc = cookies._fbc || deriveFbcFromUrl();
  const fbp = cookies._fbp;

  if (!fbc && !fbp) return;

  // Write once per session per id pair — this runs on every page load.
  const stamp = `kb:fbids:${fbc || ''}|${fbp || ''}`;
  try {
    if (sessionStorage.getItem(stamp)) return;
  } catch {
    // Private mode / storage disabled: fall through and just write.
  }

  /** @type {Record<string, string>} */
  const attributes = {};
  if (fbc) attributes._fbc = fbc;
  if (fbp) attributes._fbp = fbp;

  const root = window.Shopify?.routes?.root || '/';

  try {
    const response = await fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ attributes }),
    });

    if (response.ok) sessionStorage.setItem(stamp, '1');
  } catch {
    // Attribution enrichment only — never block or surface to the shopper.
  }
}

// Let the Meta pixel write its cookies first, then capture.
const DELAY_MS = 1200;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => setTimeout(persistClickIds, DELAY_MS));
} else {
  setTimeout(persistClickIds, DELAY_MS);
}
